import { json, redirect, type ActionFunctionArgs, type LoaderFunctionArgs } from '@remix-run/cloudflare';
import { Form, Link, useActionData, useNavigation, useSearchParams } from '@remix-run/react';
import {
  getSession,
  isEmailAuthorized,
  createMagicLink,
  sendMagicLinkEmail,
  loginWithPassword,
} from '~/lib/auth.server';
import { createTotpChallenge, userHas2fa } from '~/lib/auth-2fa.server';
import { loginSchema } from '~/lib/validation';
import { createLogger } from '~/lib/logger.server';
import { CheckCircle2 } from 'lucide-react';

const log = createLogger('admin.auth.login');
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '~/components/ui/tabs';

export async function loader({ request, context }: LoaderFunctionArgs) {
  const session = await getSession(request, context.env);
  if (session) throw redirect('/');
  return null;
}

export async function action({ request, context }: ActionFunctionArgs) {
  const requestId = request.headers.get('x-request-id') ?? crypto.randomUUID();
  const reqLog = log.child({ requestId });
  const formData = await request.formData();
  const raw = {
    method: formData.get('method'),
    email: (formData.get('email') as string)?.trim().toLowerCase(),
    password: formData.get('password') || undefined,
  };

  const parsed = loginSchema.safeParse(raw);
  if (!parsed.success) {
    const method = String(raw.method || 'password');
    return json({ error: 'Dados invalidos.', method }, { status: 400 });
  }

  const { email, method, password } = parsed.data;
  reqLog.info('login.attempt', { method, email });

  if (method === 'password') {
    if (!password) {
      return json({ error: 'Senha obrigatoria', method }, { status: 400 });
    }

    try {
      const ip = request.headers.get('CF-Connecting-IP') || undefined;
      const userAgent = request.headers.get('User-Agent') || undefined;
      const result = await loginWithPassword(context.env, email, password, ip);

      if ('error' in result) {
        reqLog.info('login.failed', { method, email, reason: result.error });
        return json({ error: result.error, method }, { status: 401 });
      }

      // 2FA check — if user has TOTP enabled, defer session creation
      if (await userHas2fa(context.env, result.user.id)) {
        const challengeId = await createTotpChallenge(context.env, {
          userId: result.user.id,
          email: result.user.email,
          via: 'password',
          ip,
          userAgent,
          createdAt: Date.now(),
        });
        reqLog.info('login.2fa.challenge', { userId: result.user.id });
        return redirect(`/login/2fa?ch=${encodeURIComponent(challengeId)}`);
      }

      reqLog.info('login.success', { method, email });
      return redirect('/', {
        headers: { 'Set-Cookie': result.cookie },
      });
    } catch (err) {
      reqLog.error('login.error', { method, email, error: String(err) });
      return json({ error: 'Erro interno no login. Tente novamente.', method }, { status: 500 });
    }
  }

  // method === 'email' (magic link)
  // Allow if in ALLOWED_EMAILS or if user was invited (exists in DB)
  if (!(await isEmailAuthorized(context.env, email))) {
    reqLog.info('login.unauthorized', { method, email });
    return json({ error: 'Email nao autorizado', method }, { status: 403 });
  }

  try {
    const link = await createMagicLink(context.env, email);
    const requestIp = request.headers.get('CF-Connecting-IP') || undefined;
    const result = await sendMagicLinkEmail(context.env, email, link, { requestIp });
    if (!result.ok) {
      reqLog.error('login.magic-link.send_failed', { email, error: result.error });
      return json(
        { error: result.error ?? 'Erro ao enviar email. Tente novamente.', method },
        { status: 500 },
      );
    }
    reqLog.info('login.magic-link.sent', { email });
    return json({ success: true, email, method });
  } catch (err) {
    reqLog.error('login.magic-link.error', { email, error: String(err) });
    return json({ error: 'Erro ao enviar email. Tente novamente.', method }, { status: 500 });
  }
}

export default function LoginPage() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const isSubmitting = navigation.state === 'submitting';
  const errorParam = searchParams.get('error');
  const justReset = searchParams.get('reset') === '1';

  if (actionData && 'success' in actionData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardContent className="pt-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-foreground">Verifique seu email</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Enviamos um link de acesso para <strong>{actionData.email}</strong>
            </p>
            <p className="mt-4 text-xs text-muted-foreground">O link expira em 10 minutos.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle>Admin</CardTitle>
          <CardDescription>Entre para acessar o painel</CardDescription>
        </CardHeader>
        <CardContent>
          {errorParam === 'invalid' && (
            <p className="mb-4 text-sm text-destructive">Link invalido ou expirado.</p>
          )}
          {errorParam === 'expired' && (
            <p className="mb-4 text-sm text-destructive">Link expirado. Solicite um novo.</p>
          )}
          {justReset && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800 dark:border-green-700 dark:bg-green-950 dark:text-green-200">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <p>Senha redefinida. Entre com sua nova senha.</p>
            </div>
          )}

          <Tabs defaultValue="password">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="password">Senha</TabsTrigger>
              <TabsTrigger value="email">Magic Link</TabsTrigger>
            </TabsList>

            <TabsContent value="password">
              <Form method="post" className="space-y-4 pt-2">
                <input type="hidden" name="method" value="password" />
                <div className="space-y-2">
                  <label htmlFor="email-password" className="block text-sm font-medium text-foreground">
                    Email
                  </label>
                  <Input
                    id="email-password"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    placeholder="seu@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <label htmlFor="password" className="block text-sm font-medium text-foreground">
                      Senha
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-muted-foreground underline hover:text-foreground"
                    >
                      Esqueci minha senha
                    </Link>
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    placeholder="Sua senha"
                  />
                </div>
                {actionData && 'error' in actionData && actionData.method === 'password' && (
                  <p className="text-sm text-destructive">{actionData.error}</p>
                )}
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Entrando...' : 'Entrar'}
                </Button>
              </Form>
            </TabsContent>

            <TabsContent value="email">
              <Form method="post" className="space-y-4 pt-2">
                <input type="hidden" name="method" value="email" />
                <div className="space-y-2">
                  <label htmlFor="email-magic" className="block text-sm font-medium text-foreground">
                    Email
                  </label>
                  <Input
                    id="email-magic"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="seu@email.com"
                  />
                </div>
                {actionData && 'error' in actionData && actionData.method === 'email' && (
                  <p className="text-sm text-destructive">{actionData.error}</p>
                )}
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Enviando...' : 'Enviar link de acesso'}
                </Button>
              </Form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
